import type { FredTransform } from './fred-transforms';

export type { FredTransform };
export type FredSection = 'rates' | 'inflation' | 'labor' | 'risk' | 'growth';

export interface FredSeriesConfig {
  id: string;
  fredSeriesId: string;
  title: string;
  description: string;
  unit: string;
  color: string;
  chartType: 'line' | 'bar';
  transform: FredTransform;
  section: FredSection;
}

export const FRED_SERIES: FredSeriesConfig[] = [
  // Rates & Yield Curve
  {
    id: 'T10Y2Y',
    fredSeriesId: 'T10Y2Y',
    title: '10Y–2Y Treasury Spread',
    description: 'Spread between 10-year and 2-year Treasury yields. Inversion has historically preceded recessions.',
    unit: '%',
    color: '#3B82F6',
    chartType: 'line',
    transform: 'none',
    section: 'rates',
  },
  {
    id: 'DFF',
    fredSeriesId: 'DFF',
    title: 'Fed Funds Rate',
    description: 'The interest rate at which depository institutions trade federal funds overnight.',
    unit: '%',
    color: '#10B981',
    chartType: 'line',
    transform: 'none',
    section: 'rates',
  },
  {
    id: 'SOFR',
    fredSeriesId: 'SOFR',
    title: 'SOFR',
    description:
      'Secured Overnight Financing Rate — benchmark overnight rate for dollar-denominated loans collateralized by Treasury securities. Published daily by the NY Fed.',
    unit: '%',
    color: '#6366F1',
    chartType: 'line',
    transform: 'none',
    section: 'rates',
  },
  // Inflation
  {
    id: 'CPIAUCSL_YOY',
    fredSeriesId: 'CPIAUCSL',
    title: 'CPI (YoY)',
    description: 'Consumer Price Index for All Urban Consumers, year-over-year change.',
    unit: '%',
    color: '#F59E0B',
    chartType: 'line',
    transform: 'yoy_pct',
    section: 'inflation',
  },
  {
    id: 'PCEPILFE_YOY',
    fredSeriesId: 'PCEPILFE',
    title: 'Core PCE (YoY)',
    description: "Personal Consumption Expenditures excluding food and energy. The Fed's preferred inflation gauge.",
    unit: '%',
    color: '#EF4444',
    chartType: 'line',
    transform: 'yoy_pct',
    section: 'inflation',
  },
  {
    id: 'T10YIE',
    fredSeriesId: 'T10YIE',
    title: '10Y Breakeven Inflation',
    description: 'Market-implied expected inflation derived from 10-year Treasury vs TIPS yields.',
    unit: '%',
    color: '#A78BFA',
    chartType: 'line',
    transform: 'none',
    section: 'inflation',
  },
  // Labor
  {
    id: 'UNRATE',
    fredSeriesId: 'UNRATE',
    title: 'Unemployment Rate',
    description: 'Civilian unemployment rate, seasonally adjusted.',
    unit: '%',
    color: '#06B6D4',
    chartType: 'line',
    transform: 'none',
    section: 'labor',
  },
  {
    id: 'PAYEMS_MOM',
    fredSeriesId: 'PAYEMS',
    title: 'Non-farm Payrolls (MoM change)',
    description: 'Month-over-month change in total non-farm employment, in thousands.',
    unit: 'K',
    color: '#22C55E',
    chartType: 'bar',
    transform: 'mom_diff',
    section: 'labor',
  },
  {
    id: 'ICSA',
    fredSeriesId: 'ICSA',
    title: 'Initial Jobless Claims',
    description: 'Weekly count of new applications for unemployment insurance. Leading labor market indicator.',
    unit: 'K',
    color: '#FB7185',
    chartType: 'line',
    transform: 'none',
    section: 'labor',
  },
  // Risk & Liquidity
  {
    id: 'BAMLH0A0HYM2',
    fredSeriesId: 'BAMLH0A0HYM2',
    title: 'High-Yield Credit Spread',
    description: 'ICE BofA US High Yield Index Option-Adjusted Spread. Widens during stress.',
    unit: '%',
    color: '#F97316',
    chartType: 'line',
    transform: 'none',
    section: 'risk',
  },
  {
    id: 'WALCL',
    fredSeriesId: 'WALCL',
    title: 'Fed Balance Sheet',
    description: 'Total assets held by the Federal Reserve. Proxy for system liquidity.',
    unit: '$T',
    color: '#8B5CF6',
    chartType: 'line',
    transform: 'none',
    section: 'risk',
  },
  // Growth & Dollar
  {
    id: 'GDPC1_YOY',
    fredSeriesId: 'GDPC1',
    title: 'Real GDP (YoY)',
    description: 'Real Gross Domestic Product, year-over-year percent change.',
    unit: '%',
    color: '#14B8A6',
    chartType: 'line',
    transform: 'yoy_pct',
    section: 'growth',
  },
  {
    id: 'DTWEXBGS',
    fredSeriesId: 'DTWEXBGS',
    title: 'Trade-Weighted USD',
    description: 'Nominal Broad U.S. Dollar Index against a basket of major trading partner currencies.',
    unit: '',
    color: '#FBBF24',
    chartType: 'line',
    transform: 'none',
    section: 'growth',
  },
];

export const FRED_SECTIONS: { id: FredSection; label: string }[] = [
  { id: 'rates', label: 'Rates & Yield Curve' },
  { id: 'inflation', label: 'Inflation' },
  { id: 'labor', label: 'Labor' },
  { id: 'risk', label: 'Risk & Liquidity' },
  { id: 'growth', label: 'Growth & Dollar' },
];

// Yield-curve maturities are fetched directly by YieldCurveSnapshot, not via the catalog.
const YIELD_CURVE_IDS = ['DGS3MO', 'DGS2', 'DGS5', 'DGS10', 'DGS30'];

export const FRED_ALLOWLIST: ReadonlySet<string> = new Set<string>([
  ...FRED_SERIES.map((s) => s.id),
  ...FRED_SERIES.map((s) => s.fredSeriesId),
  ...YIELD_CURVE_IDS,
  'USREC',
]);

export const FRED_RANGES = ['1Y', '5Y', '10Y', 'Max'] as const;
export type FredRange = (typeof FRED_RANGES)[number];

const SERIES_BY_ID = new Map(FRED_SERIES.map((s) => [s.id, s]));

export function resolveFredSeriesId(id: string): string | null {
  if (!FRED_ALLOWLIST.has(id)) return null;
  return SERIES_BY_ID.get(id)?.fredSeriesId ?? id;
}

export function getTransformForId(id: string): FredTransform {
  const entry = SERIES_BY_ID.get(id);
  return entry?.transform ?? 'none';
}
